import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { capitalizeFirstLetter, formatDateToString, parseWallDate } from '@core/utils';
import { DateFormats } from '@core/enums';
import { SzCalendarComponent } from '../../forms/sz-calendar';
import { SzTimeWheelComponent } from '../../forms/sz-time-wheel';
import { SzToggleComponent } from '../../forms/sz-toggle';
import { SzButtonComponent } from '../../controls/sz-button';
import { SzBottomSheetComponent } from '../sz-bottom-sheet';

export interface SzDatePickerResult {
  readonly date: string;
  readonly time: string | null;
}

type SzDatePickerStep = 'date' | 'time';

const DEFAULT_TIME: string = '12:00';

@Component({
  selector: 'sz-date-picker',
  templateUrl: './sz-date-picker.component.html',
  styleUrls: ['./sz-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    SzBottomSheetComponent,
    SzCalendarComponent,
    SzTimeWheelComponent,
    SzToggleComponent,
    SzButtonComponent
  ]
})
export class SzDatePickerComponent {
  readonly open: InputSignal<boolean> = input<boolean>(false);
  readonly date: InputSignal<string> = input<string>('');
  readonly time: InputSignal<string | null> = input<string | null>(null);
  readonly withTime: InputSignal<boolean> = input<boolean>(true);
  readonly min: InputSignal<string | null> = input<string | null>(null);
  readonly max: InputSignal<string | null> = input<string | null>(null);

  @Output() readonly confirm: EventEmitter<SzDatePickerResult> = new EventEmitter<SzDatePickerResult>();
  @Output() readonly dismiss: EventEmitter<void> = new EventEmitter<void>();

  readonly localDate: WritableSignal<string> = signal<string>('');
  readonly localTime: WritableSignal<string | null> = signal<string | null>(null);
  readonly timeEnabled: WritableSignal<boolean> = signal<boolean>(false);
  readonly step: WritableSignal<SzDatePickerStep> = signal<SzDatePickerStep>('date');

  constructor() {
    // Otwarcie kopiuje aktualne wartości i wraca do kroku 1 - commit dopiero na „Gotowe”.
    effect((): void => {
      if (this.open()) {
        this.localDate.set(this.date());
        this.localTime.set(this.time());
        this.timeEnabled.set(this.time() !== null);
        this.step.set('date');
      }
    });
  }

  readonly headerLabel: Signal<string> = computed((): string => {
    const iso: string = this.localDate();
    if (iso.length === 0) return '';
    // parseWallDate → lokalna północ; formatDateToString(iso) coerce'uje string przez new Date (UTC) i przesuwa dzień
    return capitalizeFirstLetter(formatDateToString(parseWallDate(iso), DateFormats.DayMonthNameLong));
  });

  readonly weekdayLabel: Signal<string> = computed((): string => {
    const iso: string = this.localDate();
    if (iso.length === 0) return '';
    const weekday: string = capitalizeFirstLetter(formatDateToString(parseWallDate(iso), DateFormats.WeekdayName));
    const time: string | null = this.localTime();
    return this.timeEnabled() && time !== null ? `${weekday} · ${time}` : weekday;
  });

  onDateSelect(iso: string): void {
    this.localDate.set(iso);
  }

  goToTimeStep(): void {
    if (this.localDate().length === 0) {
      return;
    }
    this.step.set('time');
  }

  backToDateStep(): void {
    this.step.set('date');
  }

  onTimeChange(value: string): void {
    this.localTime.set(value);
  }

  onTimeToggle(enabled: boolean): void {
    this.timeEnabled.set(enabled);
    if (enabled && this.localTime() === null) {
      this.localTime.set(DEFAULT_TIME);
    }
  }

  selectToday(): void {
    this.localDate.set(formatDateToString(new Date(), DateFormats.IsoDate));
  }

  done(): void {
    if (this.localDate().length === 0) {
      return;
    }
    this.confirm.emit({
      date: this.localDate(),
      time: this.timeEnabled() ? this.localTime() : null
    });
  }
}
